import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  APP_CHANNELS,
  EmailValidationRules,
  FeatureFlags,
  IDPHint,
  StorageKeys,
} from '@core/constants';
import { JWT_INSTANCE } from '@core/helpers/jwt';
import { DecodedToken } from '@core/models/decoded-token';
import { UserModel } from '@core/models/user-model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { ClientService } from '@core/services/client/client.service';
import { LoginService } from '@core/services/login/login.service';
import { Login } from '@core/state/auth/auth.actions';
import { AuthState } from '@core/state/auth/auth.reducer';
import { Store } from '@ngrx/store';
import {
  ButtonModule,
  FormValidationMessageModule,
} from '@shipmercury/mercury-ui';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { ButtonDirective, ButtonLabel } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { BehaviorSubject } from 'rxjs';
import { PublicBaseComponent } from 'src/app/public-base.component';

@Component({
  selector: 'au-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    FormValidationMessageModule,
    PasswordModule,
    CheckboxModule,
    ButtonDirective,
    AsyncPipe,
    RecaptchaModule,
    ButtonModule,
    RouterLink,
    ButtonLabel,
  ],
})
export class LoginComponent
  extends PublicBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;
  @ViewChild('turnstileRef') turnstileRef?: ElementRef<HTMLDivElement>;
  loginForm!: FormGroup;
  mercuryLogoPath = 'assets/images/logo-original.svg';

  jwt = inject(JWT_INSTANCE);
  formSubmitted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  enableSSO: boolean = true;
  showPassword: boolean = false;

  constructor(
    private authService: AuthenticationService,
    private loginService: LoginService,
    private clientService: ClientService,
    private router: Router,
    private fb: FormBuilder,
    private store: Store<AuthState>
  ) {
    super();

    if (this.featureFlagService.unleashClient?.toggles.length > 0) {
      this.initFeatureFlags();
    }
  }

  initFeatureFlags() {
    this.enableSSO = this.featureFlagService.featureFlagEnabled(
      FeatureFlags.ENABLE_SSO_LOGIN
    );
  }

  ngOnInit(): void {
    this.storageService.delete(StorageKeys.authUser);
    this.storageService.delete(StorageKeys.captchaResponse);

    this.loginForm = this.fb.group({
      email: ['', EmailValidationRules],
      password: ['', []],
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.enableRecaptchaFlag) {
      return;
    }

    if (this.useTurnstile) {
      await this.initializeTurnstile(this.turnstileRef!, (token: string) =>
        this.resolved(token)
      );
    }
  }

  get email(): FormControl {
    return this.loginForm.get('email') as FormControl;
  }

  submitLogin() {
    if (this.turnstileWidgetId) {
      this.verifiedSuccessfully = false;
      window.turnstile?.reset(this.turnstileWidgetId);
    }
    if (this.enableSSO && !this.showPassword) {
      this.sso();
    } else {
      this.login();
    }
  }

  submitRequestWithCaptcha(): void {
    this.captchaRef.execute();
  }

  resolved(captchaResponse: any) {
    if (captchaResponse) {
      this.storageService.save(captchaResponse, StorageKeys.captchaResponse);
      if (!this.useTurnstile) {
        this.submitLogin();
        this.captchaRef.reset();
      } else {
        this.verifiedSuccessfully = true;
      }
    }
  }

  login(): void {
    if (!this.loginForm.valid) {
      Object.keys(this.loginForm.controls).forEach((key) => {
        this.loginForm.get(key)?.markAsDirty();
      });

      return;
    }
    this.formSubmitted.next(true);
    const { email, password } = this.loginForm.value;
    this.loginService.login(email, password).subscribe((res) => {
      if (res && res.success) {
        this.saveToken(res.data);
        this.redirectAfterLogin();
      } else {
        this.formSubmitted.next(false);
      }
    });
  }

  sso(): void {
    if (!this.loginForm.valid) {
      return;
    }

    this.loginService.idpHint(this.email.value).subscribe((res) => {
      if (res && res.success && res.data) {
        if (
          res.data.idpHint === IDPHint.DATAMINR ||
          res.data.idpHint === IDPHint.BAUPOST ||
          res.data.idpHint === IDPHint.GMAIL
        ) {
          // redirect to sso
          this.authService.redirectToLoginPage(
            res.data.idpHint,
            this.email.value
          );
        } else {
          // OIDC, BASIC, or unknown
          this.showPassword = true;
          //this.email.setValue(this.email.value.trim());
        }
      }
    });
  }

  navigateToBookShipment() {
    this.router.navigateByUrl('ship-lite');
  }

  private saveToken(data: any) {
    let decodedToken = this.jwt.decodeToken<DecodedToken>(data.accessToken);
    if (!decodedToken) return;

    let user: UserModel = {
      name: decodedToken.name,
      email: decodedToken.email,
      customerId: decodedToken.customerId,
      authToken: data.accessToken,
      refreshToken: data.refreshToken,
      userId: decodedToken.userId,
    };
    this.store.dispatch(Login({ user }));

    return user;
  }

  private redirectAfterLogin(): void {
    this.clientService.getCurrentClient().subscribe({
      next: (res) => {
        const homePath = this.getPortalHomePagePath(res?.data);
        this.navigateWithFallback(homePath ?? '/shipping/ship');
      },
      error: () => {
        this.router.navigateByUrl('/shipping/ship');
      },
    });
  }

  private navigateWithFallback(path: string): void {
    const fallback = '/shipping/ship';
    const normalized = path.startsWith('/') ? path : `/${path}`;

    this.router.navigateByUrl(normalized).then((success) => {
      if (!success) {
        if (normalized !== fallback) this.router.navigateByUrl(fallback);
        return;
      }

      const landedUrl = this.router.url.split(/[?#]/)[0];
      const targetUrl = normalized.split(/[?#]/)[0];

      if (landedUrl !== targetUrl && normalized !== fallback) {
        this.router.navigateByUrl(fallback);
      }
    });
  }

  private getPortalHomePagePath(client: any): string | null {
    const portalChannel = client?.channel?.channels?.find(
      (c: any) => c.channelType === APP_CHANNELS.PORTAL
    );
    const homePagePath = portalChannel?.configuration?.['HOME_PAGE_PATH'];
    const path = Array.isArray(homePagePath) ? homePagePath[0] : homePagePath;
    return path?.trim() ? path : null;
  }

  ngOnDestroy(): void {
    if (this.turnstileWidgetId) {
      window.turnstile?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }
}
